import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { TaskApiService } from '../services/task-api.service';
import { TaskDrawerService } from '../services/task-drawer.service';
import { TaskErrorService } from '../services/task-error.service';
import { TaskCatalogApiService } from '../services/task-catalog-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  TaskResponse,
  TaskFilterRequest,
  TaskStatusResponse,
} from '../models/task.model';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { TaskDetailModalComponent } from '../task-detail-modal/task-detail-modal.component';
import { CompanyResponse } from '../../empresas/models/empresa.model';
import { ToastService } from '../../../shared/services/toast.service';

interface CalendarDay {
  date: Date;
  number: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  tasks: TaskResponse[];
  hasOverdue: boolean;
}

@Component({
  selector: 'app-tareas-calendar',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, TaskDetailModalComponent],
  templateUrl: './tareas-calendar.component.html'
})
export class TaskCalendarComponent implements OnInit, OnDestroy {
  allTasks: TaskResponse[] = [];
  loading = false;
  ownerFilter: 'all' | 'mine' = 'all';
  statuses: TaskStatusResponse[] = [];

  days: CalendarDay[] = [];
  selectedDate: Date = new Date();
  selectedTasks: TaskResponse[] = [];

  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();

  companies: CompanyResponse[] = [];
  loadingCompanies = new Set<number>();

  months: string[] = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  // Detail modal state
  showDetailModal = false;
  detailTaskId?: number;

  // Cache de nombres por id (para mostrar el nombre real de la asociación en el panel del día)
  private readonly companyNames = new Map<number, string>();
  private readonly dealNames = new Map<number, string>();
  private readonly leadNames = new Map<number, string>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private taskApi: TaskApiService,
    private catalogApi: TaskCatalogApiService,
    private drawerService: TaskDrawerService,
    private taskErrorService: TaskErrorService,
    private authService: AuthService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private leadApi: LeadApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarTareas();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargarTareas());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Carga =====

  private cargarCatalogos(): void {
    this.catalogApi
      .getActiveStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (d) => (this.statuses = d),
      });
  }

  cargarTareas(): void {
    this.loading = true;
    const start = new Date(this.currentYear, this.currentMonth - 1, 1);
    const end = new Date(
      this.currentYear,
      this.currentMonth + 2,
      0,
      23,
      59,
      59,
    );
    const user = this.authService.getCurrentUser();

    const filter: TaskFilterRequest = {
      page: 0,
      size: 1000,
      dueFrom: start.toISOString(),
      dueTo: end.toISOString(),
      ownerId: this.ownerFilter === 'mine' ? user?.id : undefined,
      sortBy: 'datetime_due',
      sortDirection: 'ASC',
    };

    this.taskApi
      .filterTasks(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.allTasks = page.content || [];
          this.loadCompany(page.content);
          this.generateCalendar();
          this.refreshSelectedTasks();
          this.loading = false;
        },
        error: () => {
          this.allTasks = [];
          this.generateCalendar();
          this.loading = false;
        },
      });
  }

  loadCompany(tasks: TaskResponse[]): void {
    if (!tasks) return;
    tasks.forEach((task) => {
      if (!task.intIdCompany) {
        return;
      }
      if (this.companies.some((c) => c.id === task.intIdCompany)) {
        return;
      }
      if (this.loadingCompanies.has(task.intIdCompany)) {
        return;
      }
      this.loadingCompanies.add(task.intIdCompany);
      this.empresaApi.getCompanyById(task.intIdCompany).subscribe({
        next: (company) => {
          this.companies.push(company);
        },
        error: () => {
          this.toast.error({
            title: 'Tareas',
            message: 'No se pudo cargar el nombre de la empresa asociada.',
          });
        },
      });
    });
  }

  // ===== Calendario =====

  generateCalendar(): void {
    const daysArr: CalendarDay[] = [];
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);

    const startDayOfWeek = firstDayOfMonth.getDay();
    const prevMonthLastDay = new Date(
      this.currentYear,
      this.currentMonth,
      0,
    ).getDate();

    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(
        this.currentYear,
        this.currentMonth - 1,
        prevMonthLastDay - i,
      );
      daysArr.push(this.createDayObject(date, false));
    }

    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const date = new Date(this.currentYear, this.currentMonth, i);
      daysArr.push(this.createDayObject(date, true));
    }

    const remainingDays = 42 - daysArr.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(this.currentYear, this.currentMonth + 1, i);
      daysArr.push(this.createDayObject(date, false));
    }

    this.days = daysArr;
  }

  private createDayObject(date: Date, isCurrentMonth: boolean): CalendarDay {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tasks = this.getTasksForDate(date);

    return {
      date,
      number: date.getDate(),
      isCurrentMonth,
      isToday: date.toDateString() === today.toDateString(),
      tasks,
      hasOverdue: tasks.some((t) => {
        if (!t.datetimeDue || t.status?.isFinal) return false;
        return new Date(t.datetimeDue) < new Date();
      }),
    };
  }

  getTasksForDate(date: Date): TaskResponse[] {
    return this.allTasks.filter((t) => {
      if (!t.datetimeDue) return false;
      const taskDate = new Date(t.datetimeDue);
      return (
        taskDate.getFullYear() === date.getFullYear() &&
        taskDate.getMonth() === date.getMonth() &&
        taskDate.getDate() === date.getDate()
      );
    });
  }

  selectDate(date: Date): void {
    this.selectedDate = date;
    this.refreshSelectedTasks();
  }

  private refreshSelectedTasks(): void {
    this.selectedTasks = this.getTasksForDate(this.selectedDate);
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else this.currentMonth--;
    this.cargarTareas();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else this.currentMonth++;
    this.cargarTareas();
  }

  getMonthName(): string {
    return this.months[this.currentMonth];
  }

  // ===== Acciones =====

  nuevaTareaEnFecha(d: Date): void {
    const due = new Date(d);
    due.setHours(9, 0, 0, 0);
    this.drawerService.open({ initialDueDate: due.toISOString() });
  }

  nuevaTarea(): void {
    this.drawerService.open();
  }

  editarTarea(t: TaskResponse): void {
    if (t.id != null) this.drawerService.open({ taskId: t.id });
  }

  completarTarea(t: TaskResponse, ev: Event): void {
    ev.stopPropagation();
    if (t.id == null) return;
    this.taskApi
      .completeTask(t.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.cargarTareas();
          this.drawerService.notifySaved(updatedTask);
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo completar la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  setOwnerFilter(v: 'all' | 'mine'): void {
    this.ownerFilter = v;
    this.cargarTareas();
  }

  abrirDetalle(id: number): void {
    this.detailTaskId = id;
    this.showDetailModal = true;
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.detailTaskId = undefined;
  }

  onDetailChanged(): void {
    this.cargarTareas();
  }

  vistaLista(): void {
    this.router.navigate(['/tareas']);
  }
  vistaKanban(): void {
    this.router.navigate(['/tareas/kamban']);
  }
  vistaCalendario(): void {
    this.router.navigate(['/tareas/calendario']);
  }

  // ===== Helpers de UI =====

  resolveStatus(t: TaskResponse): TaskStatusResponse | undefined {
    if (t.status?.label) return t.status;
    const id = t.status?.id ?? t.idTaskStatus;
    return id != null ? this.statuses.find((x) => x.id === id) : undefined;
  }

  getPriorityStyles(t: TaskResponse): string {
    const lvl = t.priority?.level;
    if (lvl === 1)
      return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-500';
    if (lvl === 2)
      return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border-orange-500';
    if (lvl === 3)
      return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-500';
    if (lvl === 4)
      return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-400 dark:border-gray-600';
    return 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700';
  }

  associationLabel(t: TaskResponse): string {
    if (t.intIdCompany) {
      const company = this.companies.find((c) => c.id === t.intIdCompany);
      if (company) {
        return `Empresa ${company.commercialName}`;
      }
      return `Empresa #${t.intIdCompany}`;
    }
    if (t.intIdDeal) return `Deal #${t.intIdDeal}`;
    if (t.intIdLead) return `Lead #${t.intIdLead}`;
    if (t.intIdCompany)
      return this.companyNames.get(t.intIdCompany) ?? `Empresa #${t.intIdCompany}`;
    if (t.intIdDeal)
      return this.dealNames.get(t.intIdDeal) ?? `Negocio #${t.intIdDeal}`;
    if (t.intIdLead)
      return this.leadNames.get(t.intIdLead) ?? `Lead #${t.intIdLead}`;
    return '—';
  }

  /**
   * Las asociaciones FK (intIdCompany/Deal/Lead) sólo vienen como ID. Recolectamos
   * los IDs únicos no cacheados y los resolvemos en batch para mostrar el nombre real.
   */
  private resolveAssociations(): void {
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;

    const companyIds = new Set<number>();
    const dealIds = new Set<number>();
    const leadIds = new Set<number>();

    for (const t of this.allTasks) {
      if (t.intIdCompany && !this.companyNames.has(t.intIdCompany))
        companyIds.add(t.intIdCompany);
      if (t.intIdDeal && !this.dealNames.has(t.intIdDeal))
        dealIds.add(t.intIdDeal);
      if (t.intIdLead && !this.leadNames.has(t.intIdLead))
        leadIds.add(t.intIdLead);
    }

    if (companyIds.size === 0 && dealIds.size === 0 && leadIds.size === 0)
      return;

    const calls = [
      ...Array.from(companyIds).map((id) =>
        this.empresaApi.getCompanyById(id).pipe(catchError(() => of(null))),
      ),
      ...Array.from(dealIds).map((id) =>
        this.dealApi.getDealById(id, businessId).pipe(catchError(() => of(null))),
      ),
      ...Array.from(leadIds).map((id) =>
        this.leadApi.getLeadById(id, businessId).pipe(catchError(() => of(null))),
      ),
    ];

    if (calls.length === 0) return;

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          let i = 0;
          for (const id of companyIds) {
            const c = results[i++] as { id?: number; commercialName?: string } | null;
            if (c?.commercialName) this.companyNames.set(id, c.commercialName);
          }
          for (const id of dealIds) {
            const d = results[i++] as { id?: number; name?: string } | null;
            if (d?.name) this.dealNames.set(id, d.name);
          }
          for (const id of leadIds) {
            const l = results[i++] as { id?: number; name?: string } | null;
            if (l?.name) this.leadNames.set(id, l.name);
          }
        },
      });
  }
}
